<template>
  <div class="admin-order-container">
    <!-- 搜索区域 -->
    <div class="search-area">
      <el-row :gutter="20" align="middle">
        <el-col :span="18">
          <el-space>
            <!-- 时间范围按钮 -->
            <el-button
                @click="handleTimeRange('今日')"
                :style="searchForm.timeRange === '今日' ?
                { backgroundColor: '#409EFF', borderColor: '#409EFF', color: '#fff' } :
                { color: '#adce7d', borderColor: '#adce7d' }"
                class="time-range-btn"
            >今日</el-button>

            <el-button
                @click="handleTimeRange('昨日')"
                :style="searchForm.timeRange === '昨日' ?
                { backgroundColor: '#409EFF', borderColor: '#409EFF', color: '#fff' } :
                { color: '#82baee', borderColor: '#82baee' }"
                class="time-range-btn"
            >昨日</el-button>

            <el-button
                @click="handleTimeRange('本周')"
                :style="searchForm.timeRange === '本周' ?
                { backgroundColor: '#409EFF', borderColor: '#409EFF', color: '#fff' } :
                { color: '#d47970', borderColor: '#d47970' }"
                class="time-range-btn"
            >本周</el-button>

            <el-button
                @click="handleTimeRange('本月')"
                :style="searchForm.timeRange === '本月' ?
                { backgroundColor: '#409EFF', borderColor: '#409EFF', color: '#fff' } :
                { color: '#caa807', borderColor: '#caa807', backgroundColor: '#fcca00' }"
                class="time-range-btn month-btn"
            >本月</el-button>

            <!-- 日期范围选择 -->
            <el-date-picker
                v-model="searchForm.startDate"
                type="date"
                placeholder="开始日期"
                style="width: 150px"
                @change="handleDateRangeChange"
            />
            <span>至</span>
            <el-date-picker
                v-model="searchForm.endDate"
                type="date"
                placeholder="结束日期"
                style="width: 150px"
                @change="handleDateRangeChange"
            />

            <!-- 搜索框 -->
            <el-input
                v-model="searchForm.keyword"
                placeholder="搜索订单编号、插件名称、买家"
                style="width: 300px"
                clearable
                :prefix-icon="Search"
                @clear="clearSearch"
                @keyup.enter="handleSearch"
            />
            <el-button type="primary" @click="handleSearch">搜索</el-button>
          </el-space>
        </el-col>

        <el-col :span="6" style="text-align: right">
          <div class="income-display">
            <span class="income-label">本月收益：</span>
            <span class="income-value">¥{{ monthlyIncome.toFixed(2) }}</span>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 表格区域 -->
    <el-table
        :data="tableData"
        v-loading="loading"
        class="order-table"
        stripe
        style="width: 100%"
        border
    >
      <el-table-column prop="orderNo" label="订单编号" />
      <el-table-column prop="productCode" label="插件编码" width="120" />
      <el-table-column prop="productName" label="插件名称" width="180" />
      <el-table-column prop="buyerUsername" label="购买者"  />
      <el-table-column prop="totalFee" label="金额（元）" width="100">
        <template #default="scope">
          <span style="color: #f56c6c; font-weight: 500;">
              ¥{{ scope.row.totalFee?.toFixed(2) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="purchasedDays" label="时长(天)" width="100" />
      <el-table-column prop="paidAt" label="购买时间" width="100">
        <template #default="scope">
          {{ scope.row.paidAt ? formatDateTime(scope.row.paidAt) : '-' }}
        </template>
      </el-table-column>
      <el-table-column prop="paymentMethodName" label="支付方式" width="100" />
      <el-table-column prop="paymentStatus" label="状态" >
        <template #default="scope">
          <el-tag :type="getStatusTagType(scope.row.paymentStatus)">
            {{ formatStatus(scope.row.paymentStatus) }}
          </el-tag>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页组件 -->
    <div class="pagination-container">
      <el-pagination
          v-model:current-page="currentPage"
          :page-size="pageSize"
          :total="total"
          :pager-count="5"
          layout="prev, pager, next"
          @current-change="handleCurrentChange"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive } from 'vue'
import { ElMessage } from 'element-plus'
import { Search } from '@element-plus/icons-vue'
import request from '@/utils/request'

// 响应式数据
const loading = ref(false)
const tableData = ref([])
const monthlyIncome = ref(0)

// 当前激活的时间范围按钮
const activeTimeRange = ref('')

// 分页相关数据
const currentPage = ref(1)
const pageSize = ref(10)
const total = ref(0)

// 搜索条件
const searchForm = reactive({
  timeRange: '',
  startDate: '',
  endDate: '',
  keyword: ''
})

// 获取订单列表
const getOrderList = async () => {
  loading.value = true
  try {
    const params = {
      page: currentPage.value,
      size: pageSize.value
    }

    const response = await request.get('/order/all', { params })
    tableData.value = response.data.list
    total.value = response.data.total
  } catch (error) {
    if (error.response && error.response.status === 403) {
      ElMessage.error('权限不足，无法查看订单')
    } else {
      console.error('获取订单列表失败:', error)
    }
  } finally {
    loading.value = false
  }
}

// 清空搜索
const clearSearch = () => {
  searchForm.keyword = ''
  handleSearch()
}

// 搜索订单
const handleSearch = async () => {
  if (!searchForm.keyword && !searchForm.timeRange && !searchForm.startDate) {
    resetSearch()
    return
  }

  loading.value = true
  try {
    const params = {
      keyword: searchForm.keyword,
      page: currentPage.value,
      size: pageSize.value
    }

    if (searchForm.timeRange || (searchForm.startDate && searchForm.endDate)) {
      if (searchForm.timeRange) {
        params.timeRange = searchForm.timeRange
      }
      if (searchForm.startDate && searchForm.endDate) {
        params.startDate = formatDateTime(searchForm.startDate)
        params.endDate = formatDateTime(searchForm.endDate)
      }

      const response = await request.get('/order/search/byTimeRange', { params })
      tableData.value = response.data.list
      total.value = response.data.total
    } else {
      const response = await request.get('/order/search', { params })
      tableData.value = response.data.list
      total.value = response.data.total
    }
  } catch (error) {
    if (error.response && error.response.status === 403) {
      ElMessage.error('权限不足，无法搜索订单')
    } else if (error.response && error.response.status === 400) {
      console.log("搜索参数错误")
    } else {
      console.log("搜索订单失败")
    }
    console.error(error)
  } finally {
    loading.value = false
  }
}

// 时间范围快速选择
const handleTimeRange = (range) => {
  if (activeTimeRange.value === range) {
    activeTimeRange.value = ''
    searchForm.timeRange = ''
    searchForm.startDate = ''
    searchForm.endDate = ''

    currentPage.value = 1
    getOrderList()
  } else {
    activeTimeRange.value = range
    searchForm.timeRange = range
    searchForm.startDate = ''
    searchForm.endDate = ''

    currentPage.value = 1
    handleSearch()
  }
}

// 自定义日期范围
const handleDateRangeChange = () => {
  searchForm.timeRange = ''
  activeTimeRange.value = ''

  if (searchForm.startDate && searchForm.endDate) {
    currentPage.value = 1
    handleSearch()
  }
}

// 重置搜索
const resetSearch = () => {
  searchForm.keyword = ''
  searchForm.timeRange = ''
  searchForm.startDate = ''
  searchForm.endDate = ''
  activeTimeRange.value = ''
  currentPage.value = 1
  getOrderList()
}

// 获取本月收益
const getMonthlyIncome = async () => {
  try {
    const response = await request.get('/order/monthly-income')
    monthlyIncome.value = response.data
  } catch (error) {
    if (error.response && error.response.status === 403) {
      ElMessage.error('权限不足，无法查看收益')
    } else {
      console.log("获取收益失败")
    }
    console.error(error)
  }
}

// 当前页改变
const handleCurrentChange = (newPage) => {
  currentPage.value = newPage
  if (searchForm.keyword || searchForm.timeRange || searchForm.startDate) {
    handleSearch()
  } else {
    getOrderList()
  }
}

// 支付状态格式化
const formatStatus = (status) => {
  const statusMap = {
    0: '未支付',
    1: '完成',
    2: '已退款',
    3: '已关闭'
  }
  return statusMap[status] || '未知'
}

// 状态标签类型
const getStatusTagType = (status) => {
  const typeMap = {
    0: 'warning',
    1: 'success',
    2: 'info',
    3: 'danger'
  }
  return typeMap[status] || 'info'
}

// 格式化日期时间
const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return '-'
  const date = new Date(dateTimeStr)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

onMounted(async () => {
  getOrderList()
  getMonthlyIncome()
})
</script>

<style scoped>
.admin-order-container {
  padding: 20px;
  height: 100%;
  overflow: auto;
  box-sizing: border-box;
}

/* 搜索区域样式 */
.search-area {
  margin-bottom: 20px;
  border-radius: 4px;
}

.time-range-btn {
  width: 60px;
  height: 25px;
}

.income-display {
  font-size: 16px;
}

.income-label {
  color: #606266;
}

.income-value {
  color: #e6a23c;
  font-weight: bold;
  font-size: 18px;
}

/* 表格样式 */
.order-table {
  margin-top: 20px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
}

:deep(.el-table__header) {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif;
}

:deep(.el-table__header th) {
  font-weight: 600;
  color: #1f2937;
  background-color: #f8fafc !important;
  font-size: 14px;
  letter-spacing: 0.025em;
}

:deep(.el-table__header .cell) {
  font-weight: 600;
  color: #374151;
}

/* 增强表格整体专业感 */
:deep(.el-table) {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

:deep(.el-table th),
:deep(.el-table td) {
  text-align: center;
  border-bottom: 1px solid #f3f4f6;
}

:deep(.el-table .el-table__row:hover > td) {
  background-color: #f9fafb;
}

/* 分页样式 */
.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
}

:deep(.el-pagination) {
  font-weight: normal;
}
</style>